import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { CBLogoV2 } from '../../lib/components/Icon'
import {
  clearPartnerSession,
  getPartnerHomePath,
  hasActivePartnerSession,
  markPartnerPasswordResetUsed,
} from '../../lib/utils/partner-session'
import { getPartnerApiError, partnerServices } from '../../services'
import {
  getPartnerPasswordError,
  PARTNER_PASSWORD_HINT,
  PASSWORD_ALREADY_CHANGED_MESSAGE,
} from './partner-password'

import '../login-v2/login-v2.scss'
import './partner.scss'

const PartnerSetPasswordContainer = () => {
  const navigate = useNavigate()
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!hasActivePartnerSession()) {
      navigate('/partner', { replace: true })
      return
    }
    if (getPartnerHomePath() === '/partner/investors') {
      navigate('/partner/investors', { replace: true })
      return
    }
    setReady(true)
  }, [navigate])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!newPassword) {
      setError('Enter a new password.')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Enter the same new password in both fields.')
      return
    }
    const validationError = getPartnerPasswordError(newPassword)
    if (validationError) {
      setError(validationError)
      return
    }

    setIsSubmitting(true)
    setError('')
    try {
      await partnerServices.setPassword(newPassword)
      markPartnerPasswordResetUsed()
      navigate('/partner/investors', { replace: true })
    } catch (setPasswordError) {
      const apiError = getPartnerApiError(setPasswordError, 'Unable to change your password.')
      if (apiError.status === 403 || apiError.code === 'password-reset-already-used') {
        setError(PASSWORD_ALREADY_CHANGED_MESSAGE)
      } else {
        setError(apiError.message)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleLogout = () => {
    clearPartnerSession()
    navigate('/partner', { replace: true })
  }

  if (!ready) return null

  const canSubmit = newPassword && confirmPassword
  const buttonClass = isSubmitting
    ? 'login-page__btn-primary login-page__btn-primary--loading'
    : canSubmit
    ? 'login-page__btn-primary login-page__btn-primary--enabled'
    : 'login-page__btn-primary login-page__btn-primary--disabled'

  return (
    <div className='login-page partner-login' data-testid='partner-set-password-page'>
      <aside className='login-page__brand' aria-label='Credibila partner portal'>
        <header className='login-page__brand-header'>
          <h1 className='login-page__brand-header-title'>Partner Portal</h1>
        </header>
        <div
          className='login-page__brand-hero'
          role='img'
          aria-label='Client reviewing markets on a tablet'
        />
      </aside>
      <main className='login-page__form-section' aria-label='Set your password'>
        <div className='login-page__form-card'>
          <Link to='/home' className='login-page__form-logo' aria-label='Go to home'>
            <CBLogoV2 classes={['login-page__form-logo-svg']} width='168' height='40' />
          </Link>
          <h2 className='partner-login__title'>Set your password</h2>
          <p className='partner-login__subtitle'>
            Choose a password to replace the temporary one from Credibila. You can do this once.
          </p>
          {error ? (
            <p className='partner-login__error' role='alert'>
              {error}
            </p>
          ) : null}
          <form className='partner-login__form' onSubmit={handleSubmit} noValidate>
            <div className='partner-login__field'>
              <label htmlFor='partner-new-password'>New password</label>
              <input
                id='partner-new-password'
                name='newPassword'
                type='password'
                autoComplete='new-password'
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
              />
              <p className='partner-login__hint'>{PARTNER_PASSWORD_HINT}</p>
            </div>
            <div className='partner-login__field'>
              <label htmlFor='partner-confirm-password'>Confirm new password</label>
              <input
                id='partner-confirm-password'
                name='confirmPassword'
                type='password'
                autoComplete='new-password'
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
              />
            </div>
            <button type='submit' className={buttonClass} disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save password'}
            </button>
            <button type='button' onClick={handleLogout} className='partner-login__text-button'>
              Log out
            </button>
          </form>
        </div>
      </main>
    </div>
  )
}

export { PartnerSetPasswordContainer }
